/**
 * External task systems list page — `GET /external-task-systems`.
 *
 * Fills the routing gap left by `ROUTES.EXTERNAL_TASK_SYSTEMS`, which used to mount `TaskListPage`
 * (external tasks are a different resource from external task **systems** — the systems are the
 * registered destinations, the tasks are the individual items queued against them). Follows the
 * CoOrdio list-page pattern from `ProjectListPage.tsx`: `ShellHeader` + `CordioAbpPagedDataTable`
 * with the grid owning paging, search and filtering.
 *
 * ─── Column plan ────────────────────────────────────────────────────────────────────────────────
 * field           │ header      │ width │ cell type            │ filter
 * ────────────────┼─────────────┼───────┼──────────────────────┼──────────────────────────────────
 * name            │ Name        │  22%  │ text (clickable)     │ toolbar search
 * systemType      │ Type        │  16%  │ text                 │ popover text
 * apiBaseUrl      │ Base URL    │  30%  │ text                 │ —
 * taskCount       │ Tasks       │  10%  │ number               │ —
 * isActive        │ Status      │  10%  │ custom Chip          │ status tabs → `isActive`
 *
 * The `description` column is dropped: it is only useful on the detail page and adds noise on
 * scan-heavy list pages. `createdAt`/`updatedAt` live on the detail page for the same reason.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  CordioAbpPagedDataTable,
  Chip,
  ShellHeader,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { Plus } from 'lucide-react';

import { createCortexPagedDataSource } from '../components/data-display/cortexPagedDataSource';
import { hitlService, type ExternalTaskSystemDto } from '../services/hitlService';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';

import './ExternalTaskSystemsListPage.scss';

/**
 * Quick-filter tabs over `isActive`. The All tab declares the literal `'all'` because the grid
 * builds each tab's test id as `cordio-datatable-status-tab-<value>`; an empty value emits
 * `cordio-datatable-status-tab-` and GRID-13's `…-status-tab-all` selector matches nothing.
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
] as const;

/** The synthetic toolbar key the status tabs write to. Never sent to the API as-is. */
const STATUS_TAB_PARAM_KEY = 'status';

/**
 * Turns the grid's query into the one `hitlService.getExternalTaskSystems` accepts: the synthetic
 * `status` tab value becomes `isActive` (All becomes "no filter at all" rather than a third
 * state).
 */
function toSystemQuery(query: Record<string, unknown>): Record<string, unknown> {
  const { [STATUS_TAB_PARAM_KEY]: status, ...rest } = query;

  const mapped: Record<string, unknown> = { ...rest };

  if (status === 'true' || status === 'false') {
    mapped.isActive = status === 'true';
  }

  return mapped;
}

export const ExternalTaskSystemsListPage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotificationStore();

  // NO `listVersion` HERE: the page has no mutation of its own (create/edit navigate away and
  // delete is a toast until the backend endpoint exists), so a refetch counter would be state
  // that never changes. The grid's built-in Refresh covers "show me new". Reinstate one the
  // moment `hitlService.deleteExternalTaskSystem` lands and wires up a row action.
  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<ExternalTaskSystemDto>((query) =>
        hitlService.getExternalTaskSystems(toSystemQuery(query)),
      ),
    [],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<ExternalTaskSystemDto>>>(
    () => [
      { field: 'name', header: 'Name', type: 'text', width: '22%', clickable: true },
      { field: 'systemType', header: 'Type', type: 'text', width: '16%' },
      { field: 'apiBaseUrl', header: 'Base URL', type: 'text', width: '30%' },
      { field: 'taskCount', header: 'Tasks', type: 'number', width: '10%' },
      {
        field: 'isActive',
        header: 'Status',
        type: 'custom',
        width: '10%',
        customCell: (row) => (
          <Chip label={row.isActive ? 'Active' : 'Inactive'} color={row.isActive ? 'green' : 'default'} />
        ),
      },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search external task systems by name' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'External task system status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
      filters: [
        { key: 'systemType', label: 'Type', type: 'text', placeholder: 'e.g. Jira, ServiceNow' },
      ],
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  const handleDeleteClick = useCallback(
    async (_row: ExternalTaskSystemDto) => {
      // The DELETE endpoint has not been wired up on `hitlService` yet — until it is, the row
      // action is a soft "coming soon" toast rather than a broken button. The Chip in the row's
      // Type column plus the count in the Tasks column already tell the user why they cannot
      // delete a system with active tasks attached.
      showNotification(
        'External task system deletion is not yet available. Deactivate the system to stop new tasks from being routed to it.',
        'info',
      );
    },
    [showNotification],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<ExternalTaskSystemDto>>>(
    () => [
      {
        icon: 'square-pen',
        tooltip: 'Edit system',
        onClick: (row) => navigate(ROUTES.EXTERNAL_TASK_SYSTEM_EDIT.replace(':id', row.id)),
      },
      {
        icon: 'trash-2',
        tooltip: 'Delete system',
        className: 'external-task-systems__action--destructive',
        onClick: (row) => void handleDeleteClick(row),
      },
    ],
    [navigate, handleDeleteClick],
  );

  return (
    <div className="external-task-systems" data-testid="external-task-system-list-page">
      <ShellHeader
        title="External Task Systems"
        description="Registered destinations for human-in-the-loop tasks — Jira, ServiceNow, and other queue integrations."
        data-testid="external-task-system-list-header"
        actions={
          <Button
            type="button"
            variant="default"
            size="sm"
            className="gap-2"
            data-testid="add-external-task-system-btn"
            onClick={() => navigate(ROUTES.EXTERNAL_TASK_SYSTEM_NEW)}
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            New External Task System
          </Button>
        }
      />

      <div data-testid="external-task-system-list-grid">
        <CordioAbpPagedDataTable<ExternalTaskSystemDto>
          className="external-task-systems__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => navigate(ROUTES.EXTERNAL_TASK_SYSTEM_EDIT.replace(':id', row.id))}
        />
      </div>
    </div>
  );
};
